import React, { useState, useRef, useEffect } from 'react';
import ModalComponent from 'components/Common/Modal';
import createNotification from 'components/Settings/Utilities/CreateNotification';

const ROW_TOOLTIPS = {
  custom: 'A custom email address added by you or a colleague on your team.',
  parliamentary: "This MP's official email address, as published by UK Parliament.",
  ministerial: "This MP's email address for correspondence relating to their ministerial role.",
};

const StakeholderEmailAddresses = ({ crmEmail, onSaveEmail }) => {
  const { email, originalEmailAddress, ministerialEmailAddress, isCustomEmailAddress, positionCategories } =
    crmEmail ?? {};

  const [hoveredKey, setHoveredKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [pendingDefault, setPendingDefault] = useState(null);
  const [addingCustom, setAddingCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [savingCustom, setSavingCustom] = useState(false);
  const customInputRef = useRef(null);

  useEffect(() => {
    if (addingCustom && customInputRef.current) customInputRef.current.focus();
  }, [addingCustom]);

  const rows = [];
  if (isCustomEmailAddress && email) {
    rows.push({ key: 'custom', label: 'Email (custom)', value: email, isDefault: true });
  }
  const parliamentaryValue = originalEmailAddress ?? (!isCustomEmailAddress ? email : null);
  if (parliamentaryValue) {
    rows.push({
      key: 'parliamentary',
      label: 'Email (parliamentary)',
      value: parliamentaryValue,
      isDefault: !isCustomEmailAddress,
    });
  }
  if (ministerialEmailAddress && positionCategories?.includes('Government')) {
    rows.push({ key: 'ministerial', label: 'Email (ministerial)', value: ministerialEmailAddress, isDefault: false });
  }

  const showDefaultLabels = rows.length > 1;

  const applyDefault = async (row) => {
    setSavingKey(row.key);
    try {
      await onSaveEmail(row.key === 'parliamentary' ? null : row.value);
      setPendingDefault(null);
    } catch {
    } finally {
      setSavingKey(null);
    }
  };

  const copyToClipboard = (value) => {
    navigator.clipboard
      .writeText(value)
      .then(() => createNotification('success', 'Email address has been copied'))
      .catch(() => createNotification('danger', 'Could not copy email address'));
  };

  const handleMakeDefault = (row) => {
    if (isCustomEmailAddress) {
      setPendingDefault(row);
    } else {
      applyDefault(row);
    }
  };

  const saveCustomEmail = async () => {
    if (!customValue || customValue === email) {
      setAddingCustom(false);
      return;
    }
    setSavingCustom(true);
    try {
      await onSaveEmail(customValue);
      setAddingCustom(false);
    } catch {
    } finally {
      setSavingCustom(false);
    }
  };

  return (
    <>
      {rows.map((row) => (
        <div key={row.key} className='mt-3'>
          <h4 className='category-subtitle member-info-subtitle d-flex align-items-center'>
            <span>{row.label}</span>
            <span
              className='icon-info pointer specific-left-margin-6 light-40-text paragraph-p3 d-inline-block'
              data-tooltip-content={ROW_TOOLTIPS[row.key]}
              data-tooltip-id='general-tooltip'
            />
          </h4>
          <p
            className='mb-0 member-data mt-2 d-flex align-items-center justify-content-between'
            onMouseEnter={() => setHoveredKey(row.key)}
            onMouseLeave={() => setHoveredKey((current) => (current === row.key ? null : current))}
          >
            <span className='d-flex align-items-center'>
              <span className='line-height-1-5-rem'>{row.value}</span>
              <button
                className='general-button member-email-button'
                onClick={() => copyToClipboard(row.value)}
                data-tooltip-content='Copy'
                data-tooltip-id='general-tooltip'
              >
                <span className='icon-copy-thin paragraph-p4 light-60-text font-weight-normal d-inline-block' />
              </button>
            </span>
            {showDefaultLabels &&
              hoveredKey === row.key &&
              (row.isDefault ? (
                <span className='light-60-text paragraph-p3 flex-shrink-0 ml-2'>Your default</span>
              ) : (
                <button
                  className='general-button simple-link-button paragraph-p3 flex-shrink-0 ml-2'
                  onClick={() => handleMakeDefault(row)}
                  disabled={savingKey === row.key}
                >
                  {savingKey === row.key ? <i className='fas fa-spinner fa-spin' /> : 'Make default'}
                </button>
              ))}
          </p>
        </div>
      ))}

      <div className='mt-3'>
        {addingCustom ? (
          <p className='mb-0 member-data d-flex align-items-center'>
            <input
              ref={customInputRef}
              type='text'
              value={customValue}
              disabled={savingCustom}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyDown={(e) => {
                if (savingCustom) return;
                if (e.key === 'Enter') saveCustomEmail();
                if (e.key === 'Escape') setAddingCustom(false);
              }}
              placeholder='Add a custom email'
              className='crm-table-editable-text-input flex-grow-1'
            />
            {savingCustom ? (
              <span className='ml-1 bg-light-blue rounded-circle d-flex align-items-center justify-content-center circle-button-24'>
                <i className='fas fa-spinner fa-spin text-white paragraph-p3' />
              </span>
            ) : (
              <span
                className='icon-circle-check ml-1 text-light-blue title-h2 pointer hover-opacity-85'
                onMouseDown={(e) => e.preventDefault()}
                onClick={saveCustomEmail}
              />
            )}
          </p>
        ) : (
          <button
            className='general-button simple-link-button'
            onClick={() => {
              setCustomValue(isCustomEmailAddress ? email : '');
              setAddingCustom(true);
            }}
          >
            + Add a custom email
          </button>
        )}
      </div>

      {pendingDefault && (
        <ModalComponent isOpen={!!pendingDefault} setIsOpen={() => setPendingDefault(null)} maxWidth={480}>
          <div className='p-4 pt-5 text-center'>
            <p className='mt-3 mb-4 paragraph-p1'>Warning - this will delete your custom email address</p>
            <div className='d-flex justify-content-center'>
              <button
                className='danger-button-outlined px-3 py-2 mr-3'
                onClick={() => setPendingDefault(null)}
                disabled={!!savingKey}
              >
                Cancel
              </button>
              <button
                className='danger-button px-3 py-2'
                onClick={() => applyDefault(pendingDefault)}
                disabled={savingKey === pendingDefault.key}
              >
                {savingKey === pendingDefault.key ? <i className='fas fa-spinner fa-spin' /> : 'Make default'}
              </button>
            </div>
          </div>
        </ModalComponent>
      )}
    </>
  );
};

export default StakeholderEmailAddresses;
